export const BLACKLISTED_GUARDRAIL_METRICS = [
  {
    name: 'lrpar_certified_rrpar_transport_btr',
    description:
      '% of transport sessions which have a booking created (transport sessions is those ever entered ANY of the transport flow.)\nCertified by the transport team.',
    owners: ['alakananda.giridhar'],
    type: 'ratio',
    dimensions: ['passenger_id', 'pax_exp_variant_order', 'country', 'city'],
    sources: ['ocd_certified.transport_f_funnel_session_bookingcode_v2'],
    platform: 'asi_backend',
    query_engine: 'asi_metric_platform',
    metadata: {
      display_name: '(Certified) Transport Btr',
      tags: ['is_distinct', 'count_distinct_metric'],
      vertical: 'All',
    },
    expr: 'COUNT(DISTINCT IF((mobility_home > 0 OR mobility_confirmation > 0) AND mobility_book > 0, analytics_session_id, NULL)) / COUNT(DISTINCT IF(mobility_home > 0 OR mobility_confirmation > 0, analytics_session_id, NULL))',
    is_higher_better: 1,
    data_sensitivity: 'Others',
    metric_quality: {
      last_refresh_date: '2023-12-14T07:13:27.961Z',
    },
    version: 1,
  },
  {
    name: 'food_session_conversion_rate_lrpar_2024_rrpar',
    description:
      'Food Session Conversion Rate (2024) - based on scribe_session_id',
    owners: ['jeremy.thia'],
    type: 'ratio',
    dimensions: ['passenger_id', 'pax_exp_variant_order', 'country', 'city'],
    sources: ['analytics_food.jt_food_daily_session_funnel_v2'],
    platform: 'asi_backend',
    query_engine: 'asi_metric_platform',
    metadata: {
      display_name: 'Food Session Conversion Rate (2024)',
      tags: ['is_distinct', 'count_distinct_metric'],
      vertical: 'All',
    },
    expr: 'COUNT(DISTINCT(CASE WHEN checkout > 0 THEN analytics_session_id END)) / COUNT(DISTINCT(CASE WHEN pre_order_funnel_session > 0 THEN analytics_session_id END))',
    is_higher_better: 1,
    data_sensitivity: 'Others',
    metric_quality: {
      last_refresh_date: '2023-12-14T08:26:03.215Z',
    },
    version: 1,
  },
];

export interface MetricWithDimensionFields extends Metric {
  dimension_filters: Array<{
    dimension_filter_constraints: DimensionFilterConstraint[];
  }>;
  is_exposed_join_enabled?: boolean;
  join_strategy?: 'TRANSACTING' | 'EXPOSED';
  metric_default_value?: string | number;
  is_log_transformation_enabled?: boolean;
  // Population filter metadata (for auto-applied filters)
  populationDerivedFilters?: PopulationDerivedFilter[];
  prepopulationWarnings?: PrepopulateWarning[];
}

export interface Metric {
  metric_id?: string;
  name: string;
  description?: string;
  type?: string;
  metric_unit?: string;
  metadata?: MetricMetadata;
  owners?: string[];
  sources?: string[];
  expr?: string;
  metric_quality?: MetricQuality;
  analysis_dimension?: string;
  cluster_dimension?: string;
  version?: number;
  is_local_timezone?: boolean;
}

export type DimensionFilterConstraint = {
  dimension_name: string;
  operator: string;
  type?: DimensionFilterValueType;
  values: DimensionFilterValue | DimensionFilterValue[];
  value_input_mode?: DimensionValueInputMode;
  validation_status?: DimensionFilterValidationStatus;
};

export interface MetricMetadata {
  display_name: string;
  vertical: string;
}

export interface MetricQuality {
  last_refresh_date: string;
}

// Control metric type extends MetricWithDimensionFields with candidate_id for duplicate handling
export interface ControlMetricWithFilters extends MetricWithDimensionFields {
  // Unique identifier for control metric candidates (handles duplicates with different filters)
  candidate_id?: string;
}

export interface PopulationDerivedFilter {
  dimension_name: string;
  source_facet: string;
  editable: boolean;
}

export interface PrepopulateWarning {
  dimension_name: string;
  source_facet?: string;
  reason:
    | 'country_code_not_found'
    | 'dimension_not_found'
    | 'dimension_values_not_present'
    | 'dimension_value_type_mismatch';
}

// Control metrics constants
export const MAX_CONTROL_METRICS = 8;

export class ExperimentAnalysisConfigView {
  id?: string;
  analysisName?: string;
  analysisConfig?: ExperimentAnalysisConfig;
  schemaVersion?: number;
  supersetLink?: string;
}

export class ExperimentAnalysisConfig implements MetricState {
  primary_metrics?: MetricWithDimensionFields[];
  secondary_metrics?: MetricWithDimensionFields[];
  guardrail_metrics?: MetricWithDimensionFields[];
  control_metrics?: ControlMetricWithFilters[];
  deepdive_all_dimensions?: boolean;
  enable_triggered_analysis?: boolean;
  populationFilterEvent: string;
  populationFilterState: string;
}

export class AnalysisMetric {
  id?: string;
  name: string;
  type: AnalysisMetricType;
  valueType: string;
  parameters?: { [key: string]: string };
  isPrimaryMetric: boolean;
  description?: string;
}

export enum AnalysisMetricType {
  PreDefined = 'pre-defined',
  EventBased = 'event-based',
}

export type MetricType = 'primary' | 'secondary' | 'guardrail' | 'control';

export interface MetricState {
  primary_metrics?: MetricWithDimensionFields[];
  secondary_metrics?: MetricWithDimensionFields[];
  guardrail_metrics?: MetricWithDimensionFields[];
  control_metrics?: ControlMetricWithFilters[];
}

export type DimensionFilterValue = string | number | boolean;
export type DimensionFilterValueType = 'STRING' | 'INTEGER' | 'BOOLEAN';
export type DimensionValueInputMode = 'DROPDOWN' | 'FREE_TEXT';
export type DimensionFilterValidationStatus = 'VERIFIED' | 'UNVERIFIED';

export const DEFAULT_VALUE_INPUT_MODE: DimensionValueInputMode = 'DROPDOWN';

export type DraftDimensionFilterConstraint = {
  dimension_name: string | null;
  operator: string | null;
  type?: DimensionFilterValueType;
  values: DimensionFilterValue | DimensionFilterValue[] | null;
  value_input_mode?: DimensionValueInputMode;
  validation_status?: DimensionFilterValidationStatus;
};

export interface Dimension {
  name: string;
  display_name: string;
  data_type: 'INTEGER' | 'STRING' | 'BOOLEAN' | 'UNKNOWN';
  cardinality: 'HIGH' | 'LOW';
  governed_dimension_name: string | null;
  value_input_mode?: DimensionValueInputMode;
}
